import React, {useEffect, useMemo, useState} from 'react';
import {Button, createListCollection, Input, Portal, Select, Spinner, VStack} from '@chakra-ui/react';
import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field} from "@/components";
import {getLabelError} from "@/components/list/CreateOrEditDivision";
import {useLanguage} from "@/contexts/languageContext";

export const CreateOrEditClub = (props) => {
    const clubId = props?.id;
    const {t} = useLanguage();
    const api = useApi();
    const [divisions, setDivisions] = useState(null);
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {label: '', divisionId: null},
        {
            label: {
                required: {value: true, message: t('errors.pleaseFillField')},
            },
            divisionId: {
                required: {value: true, message: t('errors.pleaseFillField')},
            },
        }
    );

    useEffect(() => {
        api.ApiService.get('division', {getAll: true})
            .then((res) => {
                if (res?.data?.type === 'success') {
                    setDivisions(res.data.value ?? []);
                }
            })
            .catch(() => setDivisions([]));
        /** Dans l'index, l'identifiant de la division est exposé sous `division` */
        if (clubId) {
            handleInputChanges({
                label: props?.label ?? '',
                divisionId: props?.division ?? null,
            });
        }
    }, [clubId]);

    const divisionsCollection = useMemo(() => createListCollection({
        items: (divisions ?? []).map((division) => ({
            label: division.label,
            value: String(division.id),
        })),
    }), [divisions]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                divisions === null
                    ? <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
                    : <VStack flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <Field
                            label={t('wrappers.mandatoryLabel', {value: t('fields.label')})}
                            errors={getLabelError(formFieldErrors.label, t)}
                            maxW="40rem"
                        >
                            <Input
                                value={formData.label}
                                onChange={(e) => handleInputChange('label', e?.target?.value ?? '')}
                                placeholder={t('fields.club')}
                            />
                        </Field>
                        <Field
                            label={t('wrappers.mandatoryLabel', {value: t('fields.division')})}
                            errors={formFieldErrors.divisionId}
                            maxW="40rem"
                        >
                            <Select.Root
                                collection={divisionsCollection}
                                value={formData.divisionId ? [String(formData.divisionId)] : []}
                                onValueChange={(e) => {
                                    const value = e.value?.[0];
                                    handleInputChange('divisionId', value ? parseInt(value, 10) : null);
                                }}
                            >
                                <Select.HiddenSelect/>
                                <Select.Control>
                                    <Select.Trigger>
                                        <Select.ValueText
                                            placeholder={t('sentences.selectEntity', {entity: t('fields.division')})}
                                        />
                                    </Select.Trigger>
                                    <Select.IndicatorGroup>
                                        <Select.Indicator/>
                                    </Select.IndicatorGroup>
                                </Select.Control>
                                <Portal>
                                    <Select.Positioner>
                                        <Select.Content>
                                            {divisionsCollection.items.map((division) => (
                                                <Select.Item item={division} key={division.value}>
                                                    {division.label}
                                                    <Select.ItemIndicator/>
                                                </Select.Item>
                                            ))}
                                        </Select.Content>
                                    </Select.Positioner>
                                </Portal>
                            </Select.Root>
                        </Field>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (clubId) {
                        return api.ApiService.update('club', clubId, data);
                    }
                    return api.ApiService.create('club', data);
                }, props?.successCallback)}
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditClub;
